import React, { useDeferredValue, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaPlus } from "react-icons/fa";
import toast from "react-hot-toast";

import adminApi from "services/admin-api";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import { DataTableSkeleton } from "components/admin";
import RightDrawer from "components/global/RightDrawer";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { DataTable } from "components/molecules/table/table";
import { PageHeader } from "components/admin/ui";
import { NewsArticleForm } from "pages/app/news/news-article-form";
import { formatDate, getApiErrorMessage } from "utils/admin";
import {
  NewsContentStack,
  NewsSection,
  NewsGridLayout,
  NewsPreviewImage,
  NewsFieldInput,
  NewsLabelText,
  NewsMetaText,
  NewsFieldSelect,
} from "pages/app/news/styles/news.page.inline.styles";

const emptyArticle = {
  title: "",
  excerpt: "",
  content: "",
  image_url: "",
  published_at: "",
  author: "",
  source_name: "",
  source_url: "",
  external_url: "",
  sport_id: "",
  team_id: "",
  player_id: "",
};

const NewsPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [sportFilter, setSportFilter] = useState("all");
  const [selectedArticle, setSelectedArticle] = useState<any | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const deferredSearch = useDeferredValue(searchValue);
  const { data: currentUser } = useQuery(buildCurrentUserQueryOptions());
  const canManageNews = currentUser?.role === "admin";
  const { data: sports = [] } = useQuery({
    queryKey: ["sports"],
    queryFn: () => adminApi.sports.list(),
  });
  const { data: teams = [] } = useQuery({
    queryKey: ["teams"],
    queryFn: () => adminApi.teams.list(),
    enabled: canManageNews,
  });
  const { data: athletes = [] } = useQuery({
    queryKey: ["athletes"],
    queryFn: () => adminApi.athletes.list(),
    enabled: canManageNews,
  });
  const { data: newsFeed, isLoading } = useQuery({
    queryKey: ["news-feed"],
    queryFn: () =>
      adminApi.news.list({
        page: 1,
        per_page: 50,
      }),
  });
  const articles = newsFeed?.articles ?? [];
  const filteredArticles = articles.filter((article: any) => {
    const matchesSearch = [article.title, article.excerpt, article.source_name, article.player_name]
      .join(" ")
      .toLowerCase()
      .includes(deferredSearch.trim().toLowerCase());
    const matchesSport = sportFilter === "all" || article.sport_name === sportFilter;
    return matchesSearch && matchesSport;
  });
  const sportOptions = useMemo(
    () => sports.map((sport: any) => ({ label: sport.name, value: sport.id })),
    [sports],
  );
  const teamOptions = useMemo(
    () => teams.map((team: any) => ({ label: team.name, value: team.id })),
    [teams],
  );
  const playerOptions = useMemo(
    () => athletes.map((athlete: any) => ({ label: athlete.full_name, value: athlete.id })),
    [athletes],
  );
  const saveMutation = useMutation({
    mutationFn: (payload: any) => {
      const normalizedPayload = {
        ...payload,
        sport_id: payload.sport_id || undefined,
        team_id: payload.team_id || undefined,
        player_id: payload.player_id || undefined,
        published_at: payload.published_at || undefined,
        image_url: payload.image_url || undefined,
        excerpt: payload.excerpt || undefined,
        content: payload.content || undefined,
        author: payload.author || undefined,
        source_url: payload.source_url || undefined,
      };

      return selectedArticle
        ? adminApi.news.update(selectedArticle.id, normalizedPayload)
        : adminApi.news.create(normalizedPayload);
    },
    onSuccess: () => {
      toast.success(selectedArticle ? "News article updated." : "News article created.");
      queryClient.invalidateQueries({
        queryKey: ["news-feed"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-news"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-fan-news"],
      });
      setSelectedArticle(null);
      setDrawerOpen(false);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to save this news article."));
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (articleId: string | number) => adminApi.news.remove(articleId),
    onSuccess: () => {
      toast.success("News article removed.");
      queryClient.invalidateQueries({
        queryKey: ["news-feed"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-news"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-fan-news"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to remove this news article."));
    },
  });
  const openEditDrawer = async (articleSummary: any) => {
    try {
      const fullArticle = await adminApi.news.get(String(articleSummary.id));
      setSelectedArticle(fullArticle);
      setDrawerOpen(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to load this news article."));
    }
  };

  return (
    <AppLayout route="/news">
      <PageHeader
        title="News"
        description="Review the athlete and team news feed that the API is currently exposing to admin and app clients."
        actions={
          canManageNews ? (
            <button
              className="inline-flex items-center gap-2 rounded-2xl bg-[var(--brand-accent)] px-4 py-3 text-sm font-medium text-white transition hover:bg-[var(--brand-accent-deep)]"
              onClick={() => {
                setSelectedArticle(null);
                setDrawerOpen(true);
              }}
              type="button"
            >
              <FaPlus size={12} />
              Add article
            </button>
          ) : undefined
        }
      />

      <DataTable
        canEdit={canManageNews}
        canRemove={canManageNews}
        columns={[
          {
            label: "Story",
            accessor: (row: any) => (
              <NewsSection>
                <NewsPreviewImage
                  alt={row.title || "News story"}
                  fallbackLabel={row.title}
                  src={row.image_url}
                  variant="news"
                />
                <NewsContentStack>
                  <NewsLabelText>{row.title}</NewsLabelText>
                  <NewsMetaText>
                    {row.excerpt || "No summary is available for this story yet."}
                  </NewsMetaText>
                </NewsContentStack>
              </NewsSection>
            ),
          },
          {
            label: "Sport",
            accessor: (row: any) => row.sport_name || "General",
          },
          {
            label: "Athlete",
            accessor: (row: any) => row.player_name || "Not linked",
          },
          {
            label: "Source",
            accessor: (row: any) => row.source_name || "Unknown",
          },
          {
            label: "Published",
            accessor: (row: any) => formatDate(row.published_at),
          },
        ]}
        data={filteredArticles}
        emptyDescription="News content will appear here when stories are seeded or synced."
        emptyTitle="No news stories match these filters"
        isLoading={isLoading}
        loadingState={<DataTableSkeleton columns={5} rows={5} showThumbnail />}
        onEdit={
          canManageNews
            ? (article) => {
                void openEditDrawer(article);
              }
            : undefined
        }
        onRemove={
          canManageNews
            ? (articleId) => {
                if (window.confirm("Delete this news article? This action cannot be undone.")) {
                  deleteMutation.mutate(String(articleId));
                }
              }
            : undefined
        }
        onView={(id) => history.push(`/news/${id}`)}
        toolbar={
          <NewsGridLayout>
            <NewsFieldInput
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search by title, excerpt, source, or athlete"
              value={searchValue}
            />
            <NewsFieldSelect
              onChange={(event) => setSportFilter(event.target.value)}
              value={sportFilter}
            >
              <option value="all">All sports</option>
              {sports.map((sport: any) => (
                <option key={sport.id} value={sport.name}>
                  {sport.name}
                </option>
              ))}
            </NewsFieldSelect>
          </NewsGridLayout>
        }
      />

      <RightDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setSelectedArticle(null);
          setDrawerOpen(false);
        }}
        title={selectedArticle ? "Edit article" : "Create article"}
      >
        <NewsArticleForm
          initialValues={
            selectedArticle
              ? {
                  title: selectedArticle.title || "",
                  excerpt: selectedArticle.excerpt || "",
                  content: selectedArticle.content || "",
                  image_url: selectedArticle.image_url || "",
                  published_at: selectedArticle.published_at || "",
                  author: selectedArticle.author || "",
                  source_name: selectedArticle.source_name || "",
                  source_url: selectedArticle.source_url || "",
                  external_url: selectedArticle.external_url || "",
                  sport_id: selectedArticle.sport_id ? String(selectedArticle.sport_id) : "",
                  team_id: selectedArticle.team_id ? String(selectedArticle.team_id) : "",
                  player_id: selectedArticle.player_id ? String(selectedArticle.player_id) : "",
                }
              : emptyArticle
          }
          isSubmitting={saveMutation.isPending}
          onCancel={() => {
            setSelectedArticle(null);
            setDrawerOpen(false);
          }}
          onSubmit={(values) => saveMutation.mutate(values)}
          playerOptions={playerOptions}
          sportOptions={sportOptions}
          submitLabel={selectedArticle ? "Save changes" : "Create article"}
          teamOptions={teamOptions}
        />
      </RightDrawer>
    </AppLayout>
  );
};

export default NewsPage;
